import {smokingSessionFromRow,smokingSessionToRow} from './session.js';
import {smokingItemToRow} from './item.js';
import {smokingStageToRow} from './stage.js';
import {stageItemTargetToRow,stageTargetToRow} from './stage-target.js';
import {sessionWoodToRow} from './wood-mix.js';
import {itemPreparationToRow} from './preparation.js';
import {journalNoteToRow} from './journal-note.js';
import {customProductToRow} from './product-catalogue.js';
import {temperatureReadingToRow} from '../live/temperature-reading.js';
import {liveEventToRow} from '../live/live-event.js';
import {liveStageTransitionToRow} from '../live/stage-transition.js';
import {liveNoteToRow} from '../live/live-note.js';
import {SMOKING_SESSION_ENTITY_TYPE} from './session-store.js';
import {SMOKING_ITEM_ENTITY_TYPE} from './item-store.js';
import {SMOKING_STAGE_ENTITY_TYPE} from './stage-store.js';
import {
  STAGE_ITEM_TARGET_ENTITY_TYPE,
  STAGE_TARGET_ENTITY_TYPE
} from './stage-target-store.js';
import {SESSION_WOOD_ENTITY_TYPE} from './wood-mix-store.js';
import {ITEM_PREPARATION_ENTITY_TYPE} from './preparation-store.js';
import {CUSTOM_PRODUCT_ENTITY_TYPE} from './custom-product-store.js';
import {TEMPERATURE_READING_ENTITY_TYPE} from '../live/temperature-reading-store.js';
import {LIVE_EVENT_ENTITY_TYPE} from '../live/live-event-store.js';
import {LIVE_STAGE_TRANSITION_ENTITY_TYPE} from '../live/stage-transition-store.js';
import {LIVE_NOTE_ENTITY_TYPE} from '../live/live-note-store.js';
import {
  JOURNAL_ITEM_NOTE_ENTITY_TYPE,
  JOURNAL_SESSION_NOTE_ENTITY_TYPE
} from './offline.js';

function requireClient(client){
  if(typeof client?.from!=='function')throw new TypeError('Supabase client is required');
  return client;
}

function asVersion(value){
  const number=Number(value);
  return Number.isInteger(number)&&number>0?number:0;
}

function remoteError(error,code='JOURNAL_REMOTE_SYNC_FAILED'){
  if(!error)return null;
  const wrapped=new Error(code);
  wrapped.code=String(error.code||code).slice(0,80);
  wrapped.cause=error;
  return wrapped;
}

function publicRow(mapper,entity){
  const row={...mapper(entity)};
  delete row.version;
  return row;
}

function queryByKeys(query,row,keyColumns){
  let next=query;
  for(const column of keyColumns){
    const value=row[column];
    if(value==null||value==='')throw new TypeError('Remote sync key '+column+' is required');
    next=next.eq(column,value);
  }
  return next;
}

function createVersionedRowAdapter(client,{table,keyColumns,toRow}){
  async function currentVersion(row){
    let query=client.from(table).select('version');
    query=queryByKeys(query,row,keyColumns);
    const {data,error}=await query.maybeSingle();
    if(error)throw remoteError(error,'JOURNAL_REMOTE_VERSION_READ_FAILED');
    return data?asVersion(data.version):null;
  }

  async function create(entity){
    const row=publicRow(toRow,entity);
    const {data,error}=await client.from(table)
      .insert(row)
      .select('version')
      .single();

    if(error){
      const version=await currentVersion(row).catch(()=>null);
      if(version!==null){
        return Object.freeze({
          status:'conflict',
          reason:'already_exists',
          remoteServerVersion:version
        });
      }
      throw remoteError(error,'JOURNAL_REMOTE_CREATE_FAILED');
    }

    return Object.freeze({ok:true,serverVersion:asVersion(data?.version)||1});
  }

  async function update(entity){
    const row=publicRow(toRow,entity);
    const expected=asVersion(entity?.serverVersion);
    if(!expected){
      return Object.freeze({
        status:'conflict',
        reason:'missing_local_version',
        remoteServerVersion:await currentVersion(row)||0
      });
    }

    let query=client.from(table).update(row);
    query=queryByKeys(query,row,keyColumns).eq('version',expected);
    const {data,error}=await query.select('version').maybeSingle();

    if(error)throw remoteError(error,'JOURNAL_REMOTE_UPDATE_FAILED');
    if(data)return Object.freeze({ok:true,serverVersion:asVersion(data.version)||expected+1});

    const version=await currentVersion(row);
    if(version===null){
      return Object.freeze({status:'conflict',reason:'remote_missing',remoteServerVersion:0});
    }
    return Object.freeze({status:'conflict',reason:'version_mismatch',remoteServerVersion:version});
  }

  async function remove(entity){
    const row=publicRow(toRow,entity);
    const expected=asVersion(entity?.serverVersion);
    if(!expected){
      const version=await currentVersion(row);
      if(version===null)return Object.freeze({ok:true});
      return Object.freeze({
        status:'conflict',
        reason:'missing_local_version',
        remoteServerVersion:version
      });
    }

    let query=client.from(table).delete();
    query=queryByKeys(query,row,keyColumns).eq('version',expected);
    const {data,error}=await query.select(keyColumns.join(',')).maybeSingle();

    if(error)throw remoteError(error,'JOURNAL_REMOTE_DELETE_FAILED');
    if(data)return Object.freeze({ok:true});

    const version=await currentVersion(row);
    if(version===null)return Object.freeze({ok:true});
    return Object.freeze({status:'conflict',reason:'version_mismatch',remoteServerVersion:version});
  }

  return Object.freeze({create,update,delete:remove});
}

function createAppendOnlyRowAdapter(client,{table,toRow}){
  function sameValue(column,expected,actual){
    if(expected==null||actual==null)return expected==null&&actual==null;
    if(column.endsWith('_at')){
      const left=Date.parse(String(expected));
      const right=Date.parse(String(actual));
      if(!Number.isNaN(left)&&!Number.isNaN(right))return left===right;
    }
    if(typeof expected==='number')return Number(actual)===expected;
    if(typeof expected==='boolean')return Boolean(actual)===expected;
    return String(actual)===String(expected);
  }

  function equivalentRow(expected,remote){
    if(!remote)return false;
    return Object.entries(expected).every(([column,value])=>sameValue(column,value,remote[column]));
  }

  async function currentRow(row){
    const columns=[...Object.keys(row),'version'].join(',');
    const {data,error}=await client.from(table)
      .select(columns)
      .eq('id',row.id)
      .maybeSingle();
    if(error)throw remoteError(error,'JOURNAL_REMOTE_VERSION_READ_FAILED');
    return data||null;
  }

  async function create(entity){
    const row=publicRow(toRow,entity);
    const {data,error}=await client.from(table)
      .insert(row)
      .select('version')
      .single();

    if(error){
      const remote=await currentRow(row).catch(()=>null);
      if(remote&&equivalentRow(row,remote)){
        return Object.freeze({
          ok:true,
          serverVersion:asVersion(remote.version)||1,
          idempotent:true
        });
      }
      if(remote){
        return Object.freeze({
          status:'conflict',
          reason:'id_collision',
          remoteServerVersion:asVersion(remote.version)||1
        });
      }
      throw remoteError(error,'JOURNAL_REMOTE_CREATE_FAILED');
    }

    return Object.freeze({ok:true,serverVersion:asVersion(data?.version)||1});
  }

  async function immutable(entity){
    return Object.freeze({
      status:'conflict',
      reason:'append_only',
      remoteServerVersion:asVersion(entity?.serverVersion)||1
    });
  }

  return Object.freeze({create,update:immutable,delete:immutable});
}

function createSmokingSessionRowAdapter(client){
  const base=createVersionedRowAdapter(client,{
    table:'smoking_sessions',
    keyColumns:['id'],
    toRow:smokingSessionToRow
  });

  async function currentBootstrapRow(id){
    const {data,error}=await client.from('smoking_sessions')
      .select('id,user_id,status,actual_start_at,active_stage_id,active_stage_started_at,version')
      .eq('id',id)
      .maybeSingle();
    if(error)throw remoteError(error,'JOURNAL_REMOTE_VERSION_READ_FAILED');
    return data||null;
  }

  async function fetch(id){
    const sessionId=String(id||'').trim();
    if(!sessionId)throw new TypeError('session id is required');
    const {data,error}=await client.from('smoking_sessions')
      .select('*')
      .eq('id',sessionId)
      .maybeSingle();
    if(error)throw remoteError(error,'JOURNAL_REMOTE_SESSION_READ_FAILED');
    if(!data)return Object.freeze({ok:false,status:'remote_missing'});
    return Object.freeze({
      ok:true,
      entity:smokingSessionFromRow(data),
      serverVersion:asVersion(data.version)||1
    });
  }

  async function create(entity){
    if(entity?.status!=='smoking'||!entity?.activeStageId){
      return base.create(entity);
    }

    // A brand-new offline Live session creates a pre-live shell first. This
    // breaks the remote FK cycle: stage rows can then reference the session,
    // after which the deferred session update points active_stage_id at the
    // now-existing active stage.
    const bootstrap={
      ...entity,
      status:'planned',
      actualStartAt:null,
      actualEndAt:null,
      activeStageId:null,
      activeStageStartedAt:null,
      stageMutationBatchId:null
    };
    const row=publicRow(smokingSessionToRow,bootstrap);
    const {data,error}=await client.from('smoking_sessions')
      .insert(row)
      .select('version')
      .single();

    if(error){
      const remote=await currentBootstrapRow(entity.id).catch(()=>null);
      if(remote
        &&String(remote.user_id)===String(entity.userId)
        &&remote.status==='planned'
        &&remote.actual_start_at==null
        &&remote.active_stage_id==null
        &&remote.active_stage_started_at==null){
        return Object.freeze({
          ok:true,
          serverVersion:asVersion(remote.version)||1,
          keepPendingUpdate:true,
          idempotent:true
        });
      }
      if(remote){
        return Object.freeze({
          status:'conflict',
          reason:'bootstrap_id_collision',
          remoteServerVersion:asVersion(remote.version)||1
        });
      }
      throw remoteError(error,'JOURNAL_REMOTE_CREATE_FAILED');
    }

    return Object.freeze({
      ok:true,
      serverVersion:asVersion(data?.version)||1,
      keepPendingUpdate:true
    });
  }

  return Object.freeze({create,update:base.update,delete:base.delete,fetch});
}

function sessionNoteToRow(entity){
  return journalNoteToRow({...entity,scopeKind:'session',scopeId:entity.scopeId||entity.id});
}

function itemNoteToRow(entity){
  return journalNoteToRow({...entity,scopeKind:'item',scopeId:entity.scopeId||entity.id});
}

export function createSupabaseJournalRemoteAdapters({client}={}){
  const db=requireClient(client);
  return Object.freeze({
    [CUSTOM_PRODUCT_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'user_custom_products',
      keyColumns:['id'],
      toRow:customProductToRow
    }),
    [SMOKING_SESSION_ENTITY_TYPE]:createSmokingSessionRowAdapter(db),
    [SMOKING_ITEM_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_items',
      keyColumns:['id'],
      toRow:smokingItemToRow
    }),
    [SMOKING_STAGE_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_stages',
      keyColumns:['id'],
      toRow:smokingStageToRow
    }),
    [STAGE_TARGET_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_stage_targets',
      keyColumns:['stage_id'],
      toRow:stageTargetToRow
    }),
    [STAGE_ITEM_TARGET_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_stage_item_targets',
      keyColumns:['stage_id','item_id'],
      toRow:stageItemTargetToRow
    }),
    [SESSION_WOOD_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_session_woods',
      keyColumns:['id'],
      toRow:sessionWoodToRow
    }),
    [ITEM_PREPARATION_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_item_preparations',
      keyColumns:['item_id'],
      toRow:itemPreparationToRow
    }),
    [JOURNAL_SESSION_NOTE_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_session_notes',
      keyColumns:['session_id'],
      toRow:sessionNoteToRow
    }),
    [JOURNAL_ITEM_NOTE_ENTITY_TYPE]:createVersionedRowAdapter(db,{
      table:'smoking_item_notes',
      keyColumns:['item_id'],
      toRow:itemNoteToRow
    }),
    [TEMPERATURE_READING_ENTITY_TYPE]:createAppendOnlyRowAdapter(db,{
      table:'smoking_temperature_readings',
      toRow:temperatureReadingToRow
    }),
    [LIVE_EVENT_ENTITY_TYPE]:createAppendOnlyRowAdapter(db,{
      table:'smoking_live_events',
      toRow:liveEventToRow
    }),
    [LIVE_NOTE_ENTITY_TYPE]:createAppendOnlyRowAdapter(db,{
      table:'smoking_live_notes',
      toRow:liveNoteToRow
    }),
    [LIVE_STAGE_TRANSITION_ENTITY_TYPE]:createAppendOnlyRowAdapter(db,{
      table:'smoking_live_stage_transitions',
      toRow:liveStageTransitionToRow
    })
  });
}
